import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  OrgMembersListResponseDto,
  OrgMembersDetailListResponseDto,
  OrganizationResponseDto,
  OrgMemberRoleResponseDto,
  OrganizationTimeTrackingResponseDto,
} from '../models/organization.dto';
import { TimeTrackingMode } from '../models/time-entry.dto';
import {
  OrgFeatureFlagsListResponseDto,
  OrgFeatureFlagResponseDto,
  UpdateOrgFeatureFlagRequestDto,
} from '../models/feature-flag.dto';
import { PresignedUrlRequestDto, PresignedUrlResponseDto } from '../models/request.dto';

@Injectable({
  providedIn: 'root',
})
export class OrganizationDashboardService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  constructor(private readonly http: HttpClient) {}

  public getOrgMembers(orgId: string): Observable<OrgMembersListResponseDto> {
    return this.http.get<OrgMembersListResponseDto>(`${this.apiUrl}/organizations/${orgId}/members`);
  }

  public removeOrgMember(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/orgmembers/${userId}`);
  }

  public deleteOrganization(orgId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/organizations/${orgId}/`);
  }

  /** GET /api/organizations/{id}/ — full organization (includes allow_domain_join, link_join_enabled, invitation_code). */
  public getOrganization(orgId: string): Observable<OrganizationResponseDto> {
    return this.http.get<OrganizationResponseDto>(`${this.apiUrl}/organizations/${orgId}/`);
  }

  /** PATCH /api/organizations/{id}/ — partial update (e.g. allow_domain_join, link_join_enabled, name, logo). */
  public updateOrganization(orgId: string, body: Record<string, unknown>): Observable<OrganizationResponseDto> {
    return this.http.patch<OrganizationResponseDto>(`${this.apiUrl}/organizations/${orgId}/`, body);
  }

  /**
   * PATCH /api/organizations/{id}/time-tracking/ — the only way to change the mode; the generic
   * organization PATCH ignores `time_tracking`. Applies to every project in the workspace.
   */
  public updateTimeTracking(orgId: string, mode: TimeTrackingMode): Observable<OrganizationTimeTrackingResponseDto> {
    return this.http.patch<OrganizationTimeTrackingResponseDto>(
      `${this.apiUrl}/organizations/${orgId}/time-tracking/`,
      { time_tracking: mode },
    );
  }

  /** GET /api/organizations/{organization_id}/members/detail/ — additive endpoint with role/projects_involved/last_active. */
  public getOrgMembersDetail(orgId: string): Observable<OrgMembersDetailListResponseDto> {
    return this.http.get<OrgMembersDetailListResponseDto>(`${this.apiUrl}/organizations/${orgId}/members/detail/`);
  }

  /** Resolves the OrgMemberRole row (id + current role) for one org member. GET /api/orgmembers/{id}/role/ — 404 if unassigned. */
  public getOrgMemberRole(orgMemberId: string): Observable<OrgMemberRoleResponseDto> {
    return this.http.get<OrgMemberRoleResponseDto>(`${this.apiUrl}/orgmembers/${orgMemberId}/role/`);
  }

  /** PATCH /api/orgmemberroles/{id}/ — body: { role }. {id} is the OrgMemberRole id, not org_member_id. */
  public updateOrgMemberRole(orgMemberRoleId: string, roleId: string): Observable<OrgMemberRoleResponseDto> {
    return this.http.patch<OrgMemberRoleResponseDto>(`${this.apiUrl}/orgmemberroles/${orgMemberRoleId}/`, {
      role: roleId,
    });
  }

  /** POST /api/attachments/presigned-url/ — get a presigned R2 upload URL. */
  public getPresignedUploadUrl(body: PresignedUrlRequestDto): Observable<PresignedUrlResponseDto> {
    return this.http.post<PresignedUrlResponseDto>(`${this.apiUrl}/attachments/presigned-url/`, body);
  }

  /** PUT the file directly to R2 (the presigned URL carries the signature; no auth header). */
  public uploadFileToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
  }

  /**
   * List feature flags for the current organization (internal features).
   * GET /api/organizations/me/feature-flags/
   */
  public getMyFeatureFlags(): Observable<OrgFeatureFlagsListResponseDto> {
    return this.http.get<OrgFeatureFlagsListResponseDto>(`${this.apiUrl}/organizations/me/feature-flags/`);
  }

  /**
   * Update enabled state of an internal feature flag for the current organization.
   * PATCH /api/organizations/me/feature-flags/{id}/
   */
  public updateMyFeatureFlag(id: string, body: UpdateOrgFeatureFlagRequestDto): Observable<OrgFeatureFlagResponseDto> {
    return this.http.patch<OrgFeatureFlagResponseDto>(`${this.apiUrl}/organizations/me/feature-flags/${id}/`, body);
  }
}
